import { ComponentPropsWithoutRef, useState } from "react";
import styles from "./Banner.module.css";
import { CoreIconButton } from "../button/Button";
import { SvgIcon, Typography, TypographyProps } from "@mui/material";
import i18nPackage from "../../i18n/i18n";
import Close from "../images/close-x.svg?react";
import Megaphone from "../images/megaphone.svg?react";
import Checkmark from "../images/checkmark.svg?react";

type CoreBannerProps = {
	onClose?: () => void;
	label?: React.ReactNode;
	children?: React.ReactNode;
	/**@default "primary" */
	color?: "primary" | "secondary" | "success";
	slotProps?: {
		bodyWrapper?: Omit<ComponentPropsWithoutRef<"div">, "children">;
		labelWrapper?: Omit<ComponentPropsWithoutRef<"div">, "children">;
		label?: Omit<TypographyProps, "children">;
	};
} & Omit<ComponentPropsWithoutRef<"div">, "children">;

export default function CoreBanner({
	onClose,
	label,
	children,
	className,
	slotProps,
	color = "primary",
	...props
}: CoreBannerProps) {
	const [closed, setClosed] = useState(false);

	const handleClose = () => {
		setClosed(true);
		setTimeout(() => onClose?.(), 300);
	};

	const renderIcon = () => {
		switch (color) {
			case "primary":
			case "secondary":
				return (
					<SvgIcon className={styles.portalInfoIcon}>
						<Megaphone />
					</SvgIcon>
				);
			case "success":
				return (
					<div className={styles.successIconWrapper}>
						<div className={styles.successCircle}>
							<Checkmark />
						</div>
					</div>
				);
			default:
				return null;
		}
	};

	return (
		<div
			{...props}
			className={`${styles.portalBanner} ${closed ? styles.portalBannerClosed : ""} ${styles[color]} ${className || ""}`}
		>
			<div
				{...slotProps?.bodyWrapper}
				className={`${styles.portalBannerChildren} ${slotProps?.bodyWrapper?.className || ""}`}
			>
				<div
					{...slotProps?.labelWrapper}
					className={`${styles.portalBannerBody} ${slotProps?.labelWrapper?.className || ""}`}
				>
					{renderIcon()}
					<Typography
						{...slotProps?.label}
						fontSize={slotProps?.label?.fontSize ?? 14}
						className={`${styles.portalBannerText} ${slotProps?.label?.className || ""}`}
					>
						{label}
					</Typography>
				</div>
				{children}
			</div>
			{onClose && (
				<CoreIconButton variant="plain" onClick={handleClose} tooltip={i18nPackage.t("coreComponents.banner.close")}>
					<SvgIcon>
						<Close />
					</SvgIcon>
				</CoreIconButton>
			)}
		</div>
	);
}
